import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import axiosClient from "../../api/axiosClient";
import { MdArrowDropUp, MdArrowDropDown } from "react-icons/md";
import "../../styles/UserForm.css";

export default function RoleMasterPermissionMatrix() {
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sortKey, setSortKey] = useState("module");
  const [sortDir, setSortDir] = useState("asc");
  const [roleName, setRoleName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // ✅ entries per page

  // ==========================================================
  // 🔧 Helpers
  // ==========================================================
  const safeString = (v) => (v == null ? "" : String(v));

  const sortList = (list, key, dir) =>
    [...list].sort((a, b) => {
      const valA = key === "roleName" ? roleName : safeString(a?.[key]);
      const valB = key === "roleName" ? roleName : safeString(b?.[key]);
      return dir === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : ""}`}>
      {sortKey === col ? (
        sortDir === "asc" ? <MdArrowDropUp /> : <MdArrowDropDown />
      ) : (
        <MdArrowDropDown style={{ opacity: 0.3 }} />
      )}
    </span>
  );

  // ==========================================================
  // 🎨 React-Select Styling
  // ==========================================================
  const selectStyles = {
    container: (base) => ({
      ...base,
      zIndex: 10,
      minWidth: 250,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      position: "absolute",
      background: "white",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }),
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderColor: "#ced4da",
      "&:hover": { borderColor: "#80bdff" },
    }),
    multiValue: (base) => ({
      ...base,
      background: "linear-gradient(90deg, #007bff, #6f42c1)",
      color: "white",
      borderRadius: "6px",
      paddingRight: "3px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: 500,
      fontSize: "0.85rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": { background: "#5a32a3", color: "white" },
    }),
  };

  // ==========================================================
  // 🧭 Load Role Masters
  // ==========================================================
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await axiosClient.get("/rolesmaster/platform/api/list");
        const data = res?.data?.data || [];
        if (Array.isArray(data)) {
          const formatted = data.map((r) => ({ value: r.id, label: r.name }));
          setRoles(formatted);
          if (formatted.length > 0) setSelectedRoles([formatted[0]]);
        }
      } catch (err) {
        console.error("❌ Error loading Role Masters:", err);
      }
    };
    loadRoles();
  }, []);

  // ==========================================================
  // 📘 Load Permissions
  // ==========================================================
  useEffect(() => {
    const loadPermissions = async () => {
      if (!selectedRoles.length) return;
      try {
        setLoading(true);
        const roleId = selectedRoles[0].value;
        const res = await axiosClient.get(
          `/api/admin/role-master-function-permissions/list/${roleId}`
        );
        const roleName = res?.data?.data?.role_master?.name || "";
        const perms = res?.data?.data?.data || [];
        setRoleName(roleName);
        setPermissions(perms);
        const mods = [...new Set(perms.map((p) => p.module))].map((m) => ({
          value: m,
          label: m,
        }));
        setModules(mods);
      } catch (err) {
        console.error("❌ Error loading permissions:", err);
      } finally {
        setLoading(false);
      }
    };
    loadPermissions();
  }, [selectedRoles]);

  // ==========================================================
  // ✏️ Toggle checkbox
  // ==========================================================
  const handleToggle = (funcId, field) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.functionality_id === funcId ? { ...p, [field]: !p[field] } : p
      )
    );
  };

  // ==========================================================
  // 💾 Save Permissions
  // ==========================================================
  const handleSave = async () => {
    if (!selectedRoles.length) return alert("Please select a Role Master first.");
    try {
      setSaving(true);
      const roleId = selectedRoles[0].value;
      const payload = permissions.map((p) => ({
        functionality_id: p.functionality_id,
        can_view: p.can_view,
        can_create: p.can_create,
        can_edit: p.can_edit,
        can_delete: p.can_delete,
        can_approve: p.can_approve,
      }));
      await axiosClient.put(
        `/api/admin/role-master-function-permissions/update/${roleId}`,
        payload
      );
      alert("✅ Permissions updated successfully!");
    } catch (err) {
      console.error("❌ Error saving permissions:", err);
      alert("Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // 🔄 Sync Platform → Tenant
  // ==========================================================
const handleSyncToTenants = async () => {

    if (!selectedRoles.length) {
        alert("Please select a Role Master first.");
        return;
    }

    try {
        setSyncing(true);

      const res = await axiosClient.post(
          "/api/admin/rbac/sync/platform-to-tenant",
          {
              role_id: selectedRoles[0].value,
              module_codes: selectedModules.map(m => m.value)
          }
      );

      const data = res?.data?.data || {};

      alert(
        `✅ Platform → Tenant Sync Completed

  Roles Processed: ${data.roles_processed || 0}
  Permissions Updated: ${data.permissions_updated || 0}
  Permissions Inserted: ${data.permissions_inserted || 0}`
      );
    } catch (err) {
      console.error("❌ Sync failed:", err);

      alert(
        err?.response?.data?.message ||
        "Platform → Tenant synchronization failed."
      );
    } finally {
      setSyncing(false);
    }
  };

  // ==========================================================
  // 🔍 Filter + Pagination
  // ==========================================================
  const filtered = sortList(
    permissions.filter((p) => {
      const moduleMatch =
        selectedModules.length === 0 ||
        selectedModules.some(
          (m) => m.value.toLowerCase() === safeString(p.module).toLowerCase()
        );
      const q = search.toLowerCase();
      const searchMatch =
        safeString(p.functionality_name).toLowerCase().includes(q) ||
        safeString(p.code).toLowerCase().includes(q) ||
        safeString(p.type).toLowerCase().includes(q);
      return moduleMatch && searchMatch;
    }),
    sortKey,
    sortDir
  );

  const totalEntries = filtered.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(startIdx, startIdx + rowsPerPage);

  // ==========================================================
  // 🖥️ UI
  // ==========================================================
  return (
    <div className="container-fluid">
      {/* ✅ Hero Section */}
      <div
        className="users-hero shadow-sm d-flex justify-content-between align-items-center px-4 flex-wrap"
        style={{
          background: "linear-gradient(90deg, #007bff, #6f42c1)",
          color: "white",
        }}
      >
        <div>
          <div className="h4 fw-bold mb-1 text-white">Role Permission Matrix</div>
          <div className="text-white-50 small">
            Manage Role Master permissions across functionalities.
          </div>
        </div>
         <button
            onClick={handleSyncToTenants}
            disabled={syncing || selectedRoles.length === 0}
            style={{
              background: "linear-gradient(90deg, #fd7e14, #ffb347)",
              border: "none",
              padding: "10px 22px",
              borderRadius: "6px",
              fontWeight: "600",
              color: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              opacity: syncing ? 0.7 : 1,
            }}
          >
            {syncing ? (
              <>
                <Spinner animation="border" size="sm" /> Syncing...
              </>
            ) : (
              "🔄 Sync To Tenants"
            )}
          </button>
        
        
        <button
          onClick={handleSave}
          disabled={saving || !selectedRoles.length}
          style={{
            background: "linear-gradient(90deg, #28a745, #6cc57b)",
            border: "none",
            padding: "10px 22px",
            borderRadius: "6px",
            fontWeight: "600",
            color: "white",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            transition: "0.2s",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" /> Updating...
            </>
          ) : (
            "💾 Update Permissions"
          )}
        </button>
      </div>

      {/* ✅ Filters */}
      <div className="users-filter-toolbar d-flex flex-wrap align-items-center gap-2 mt-2">
        <div style={{ minWidth: 250 }}>
          <Select
            isMulti
            options={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            placeholder="Select Role Master..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div style={{ minWidth: 250 }}>
          <Select
            isMulti
            options={modules}
            value={selectedModules}
            onChange={setSelectedModules}
            placeholder="Filter by Module(s)..."
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>

      {/* ✅ Entries per page + Search Above Table */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 px-1">
        <div className="d-flex align-items-center gap-2 small text-muted">
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="form-select form-select-sm"
            style={{ width: "80px" }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          entries per page
        </div>

        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 250 }}
          placeholder="Search Functionality, Code, or Type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ✅ Table + Pagination */}
      <div className="card shadow-sm mt-2 table-responsive">
        {loading ? (
          <div className="text-center p-4">Loading permissions...</div>
        ) : currentRows.length === 0 ? (
          <div className="text-center p-4 text-muted">No data available.</div>
        ) : (
          <>
            <table className="table table-sm users-table mb-0">
              <thead className="table-light">
                <tr>
                  <th onClick={() => toggleSort("roleName")}>
                    Role Master <SortIcon col="roleName" />
                  </th>
                  <th onClick={() => toggleSort("functionality_name")}>
                    Functionality <SortIcon col="functionality_name" />
                  </th>
                  <th onClick={() => toggleSort("code")}>
                    Code <SortIcon col="code" />
                  </th>
                  <th onClick={() => toggleSort("module")}>
                    Module <SortIcon col="module" />
                  </th>
                  <th onClick={() => toggleSort("type")}>
                    Type <SortIcon col="type" />
                  </th>
                  <th className="text-center">View</th>
                  <th className="text-center">Create</th>
                  <th className="text-center">Edit</th>
                  <th className="text-center">Delete</th>
                  <th className="text-center">Approve</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((p) => (
                  <tr key={p.functionality_id}>
                    <td>{roleName}</td>
                    <td>{p.functionality_name}</td>
                    <td>{p.code}</td>
                    <td>{p.module}</td>
                    <td>{p.type}</td>
                    {["can_view", "can_create", "can_edit", "can_delete", "can_approve"].map(
                      (field) => (
                        <td key={field} className="text-center">
                          <input
                            type="checkbox"
                            checked={p[field]}
                            onChange={() => handleToggle(p.functionality_id, field)}
                            className="permission-checkbox"
                          />
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* ✅ Pagination Footer */}
        {!loading && currentRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center p-2 flex-wrap">
            <div className="small text-muted">
              Showing {startIdx + 1} to{" "}
              {Math.min(startIdx + rowsPerPage, totalEntries)} of {totalEntries} entries
            </div>

            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)}>
                  «
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ‹
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  ›
                </button>
              </li>
              <li
                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(totalPages)}
                >
                  »
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      <style>{`
        input.permission-checkbox:checked { accent-color: #28a745; }
        .pagination .page-link { color: #007bff; cursor: pointer; }
        .pagination .active .page-link {
          background-color: #007bff;
          color: white;
          border-color: #007bff;
        }
        th { cursor: pointer; user-select: none; }
        th:hover { background-color: #f1f1f1; }
      `}</style>
    </div>
  );
}
